/**
 * What the Photo Booth accepts — photos (JPG / PNG / WebP) and PDF files — checked in the browser BEFORE anything is sent.
 * The server checks again from the bytes; this gives the sender (and the shop's own "add a file" box) an immediate answer.
 *
 * The type is decided from the file's first bytes. The type the browser reports has to agree with them — or be empty / generic, which
 * some phone file managers report for a PDF, in which case the bytes decide alone. The extension is never used.
 */

export const BOOTH_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const BOOTH_PHOTO_ACCEPT = BOOTH_PHOTO_TYPES.join(','); // a shop that takes photos only
export const BOOTH_ACCEPT = `${BOOTH_PHOTO_ACCEPT},application/pdf`; // …and one that also takes PDFs
export const MAX_BOOTH_PHOTO_BYTES = 12 * 1024 * 1024;
export const MAX_BOOTH_PDF_BYTES = 10 * 1024 * 1024;

const startsWith = (bytes, signature, offset = 0) => signature.every((b, i) => bytes[offset + i] === b);

/** 'image' | 'pdf' | null from the file's leading bytes. */
export async function sniffBoothFile(file) {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    if (startsWith(head, [0x25, 0x50, 0x44, 0x46, 0x2d])) return 'pdf'; // %PDF-
    if (startsWith(head, [0xff, 0xd8, 0xff])) return 'image'; // JPEG
    if (startsWith(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image'; // PNG
    if (startsWith(head, [0x52, 0x49, 0x46, 0x46]) && startsWith(head, [0x57, 0x45, 0x42, 0x50], 8)) return 'image'; // WebP (RIFF….WEBP)
    return null;
}

/**
 * → { ok: true, kind: 'image' | 'pdf' }  |  { ok: false, reason: 'empty' | 'type' | 'tooLarge', kind? }
 * `allowPdf: false` (a shop that takes photos only) turns a real PDF into a plain "not accepted" answer.
 */
export async function checkBoothFile(file, { allowPdf = true } = {}) {
    if (!file || !file.size) return { ok: false, reason: 'empty' };
    const kind = await sniffBoothFile(file).catch(() => null);
    if (!kind || (kind === 'pdf' && !allowPdf)) return { ok: false, reason: 'type' };

    const claimed = file.type || '';
    const generic = !claimed || claimed === 'application/octet-stream';
    const agrees = kind === 'pdf' ? claimed === 'application/pdf' : BOOTH_PHOTO_TYPES.includes(claimed);
    if (!generic && !agrees) return { ok: false, reason: 'type' }; // says one thing, is another

    const limit = kind === 'pdf' ? MAX_BOOTH_PDF_BYTES : MAX_BOOTH_PHOTO_BYTES;
    if (file.size > limit) return { ok: false, reason: 'tooLarge', kind };
    return { ok: true, kind };
}

/** 1.2 MB / 340 KB — for a file chip. */
export function formatBoothSize(bytes) {
    if (!bytes) return '';
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
